// "Remind me later" for the staff MFA enrollment gate.
//
// Requiring a second factor before a brand-new school owner can see ANYTHING
// is the worst possible moment to demand it — they've just accepted an invite
// and haven't seen the product yet. So the gate is deferrable: staff can skip
// it, use the app, and get nagged by a persistent banner until they enrol.
// The deferral expires, so the gate comes back and this stays a nudge toward
// enrolment rather than a permanent opt-out.
//
// Deliberately client-side/localStorage: this is a UX nudge, not a security
// boundary. The real boundary is server-side authorization — a deferral can
// only delay the prompt, never grant access to anything.

const KEY_PREFIX = 'hypebook.schoolAdmin.mfaDeferredUntil.';

/** How long a "remind me later" lasts before the gate reappears. */
export const MFA_GRACE_DAYS = 7;

function key(userId: string): string {
  return `${KEY_PREFIX}${userId}`;
}

/** Start (or restart) the grace period for this user. */
export function deferMfaEnrollment(userId: string): void {
  try {
    const until = Date.now() + MFA_GRACE_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(key(userId), String(until));
  } catch {
    // Storage unavailable (private mode / blocked) — the gate simply stays up.
  }
}

/** True while this user's deferral is still valid. Expired entries self-clean. */
export function isMfaDeferred(userId: string | null | undefined): boolean {
  if (!userId) return false;
  try {
    const raw = localStorage.getItem(key(userId));
    if (!raw) return false;
    const until = Number(raw);
    if (!Number.isFinite(until)) return false;
    if (Date.now() >= until) {
      localStorage.removeItem(key(userId));
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Drop the deferral — called once the user actually enrols. */
export function clearMfaDeferral(userId: string | null | undefined): void {
  if (!userId) return;
  try {
    localStorage.removeItem(key(userId));
  } catch {
    // no-op
  }
}
